import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';

import { COLD_CHAIN_PROFILES } from '@/shared/lib/cold-chain';

import type { ConsignmentLineDetailsFormValues } from './types';

type UseColdChainFieldsParams = Pick<UseFormReturn<ConsignmentLineDetailsFormValues>, 'control' | 'setValue'>;

export const useColdChainFields = ({ control, setValue }: UseColdChainFieldsParams) => {
  const { t } = useTranslation();
  const [coldChain, profile] = useWatch({ control, name: ['coldChain', 'coldChainProfile'] });

  const profileOptions = useMemo(
    () => COLD_CHAIN_PROFILES.map(({ id, labelKey }) => ({ value: id, label: t(labelKey) })),
    [t]
  );

  /** A preset writes its bounds; Other keeps the previous ones to edit, as legacy `track.js` */
  const applyProfileBounds = useCallback(
    (profileId: string) => {
      const preset = COLD_CHAIN_PROFILES.find(({ id }) => id === profileId);
      if (!preset || preset.id === 'OTHER') return;
      setValue('coldMinTemp', preset.min, { shouldDirty: true });
      setValue('coldMaxTemp', preset.max, { shouldDirty: true });
    },
    [setValue]
  );

  return { coldChain, isOther: profile === 'OTHER', profileOptions, applyProfileBounds };
};
